/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import Layout from "../../layout/Main/Layout";
import CustomTable from "../../components/Custom/Table/CustomTable";
import { get, patch } from "../../config/axios";
import { Typography } from "@mui/material";
import Searchbar from "../../components/Custom/SearchBar/Searchbar";
import DeleteModal from "../../components/Custom/DeleteModal/DeleteModal";
import { useDebouncedValue } from "../../helper/debounce";
import { toastMessage } from "../../utils/toastMessage";
import {
  reportTableColumns,
} from "../../constants/ReportsPage";
const Users = () => {
  const [users, setUsers] = useState([]);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteUser, setDeleteUser] = useState("");
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState("");
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [totalData, setTotalData] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const debouncedSearch = useDebouncedValue(search, 500);

  const fetchUsers = async (searchValue) => {
    try {
      console.log("🔵 [Reports] Fetching reports:", { searchValue, page, limit: 10 });
      
      setLoading(true);
      
      // Build query parameters according to API spec
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "10"
      });
      
      if (searchValue && searchValue.trim()) {
        params.append("search", searchValue.trim());
      }
      
      const apiUrl = `/api/dashboard/appFeatures/getReports?${params.toString()}`;
      console.log("🌐 [Reports] API Request URL:", apiUrl);
      
      // Check authentication token
      const token = localStorage.getItem("token");
      console.log("🔐 [Reports] Auth token exists?", !!token);
      console.log("🔐 [Reports] Auth token length:", token ? token.length : 0);
      console.log("🔐 [Reports] Auth token preview:", token ? `${token.substring(0, 20)}...` : "N/A");
      
      const res = await get(apiUrl);
      
      // 🔍 COMPREHENSIVE DEBUG LOGGING
      console.log("🔍 ========== DEBUG START ==========");
      console.log("🔍 RAW RESPONSE:", res);
      console.log("🔍 Response type:", typeof res);
      console.log("🔍 Response is null?", res === null);
      console.log("🔍 Response is undefined?", res === undefined);
      console.log("🔍 Response keys:", res ? Object.keys(res) : "N/A");
      console.log("🔍 Has 'data' key?", res?.data !== undefined);
      console.log("🔍 Has 'response' key?", res?.response !== undefined);
      console.log("🔍 Has 'error' key?", res?.error !== undefined);
      console.log("🔍 res.data value:", res?.data);
      console.log("🔍 res.data type:", typeof res?.data);
      console.log("🔍 res.data is array?", Array.isArray(res?.data));
      console.log("🔍 res.data length:", Array.isArray(res?.data) ? res.data.length : "N/A");
      console.log("🔍 res.response value:", res?.response);
      console.log("🔍 res.status value:", res?.status);
      console.log("🔍 res.errorType value:", res?.errorType);
      console.log("🔍 Full response JSON:", JSON.stringify(res, null, 2));
      console.log("🔍 ========== DEBUG END ==========");
      
      // Check if response is an AxiosError that wasn't caught properly
      if (res?.isAxiosError || res?.name === "AxiosError") {
        const status = res?.response?.status || res?.status || "Unknown";
        const errorMsg = status === 404 
          ? "Reports endpoint not found. Please contact the backend team to implement the API endpoint."
          : `Request failed with status ${status}. Please try again later.`;
        
        console.error("❌ [Reports] AxiosError:", {
          status,
          message: res?.message,
          response: res?.response
        });
        
        toastMessage(errorMsg, "error");
        setUsers([]);
        setLoading(false);
        return;
      }
      
      // Check if response is an error object (from axios error handler)
      if (res?.response === "Failure") {
        let errorMsg = "Failed to load reports";
        
        // Handle different error scenarios
        if (res?.status === 404) {
          errorMsg = "Reports endpoint not found. Please contact the backend team to implement the API endpoint.";
        } else if (res?.status === 401) {
          errorMsg = "Unauthorized. Please log in again.";
        } else if (res?.status === 500) {
          errorMsg = "Server error. Please try again later.";
        } else if (res?.message) {
          // Try to extract meaningful error message
          const message = res.message;
          // Check if it's HTML (404 pages often return HTML)
          if (message.includes("<!DOCTYPE") || message.includes("<html")) {
            errorMsg = `API endpoint not found (${res?.status || "Unknown"}). The backend endpoint may not be implemented yet.`;
          } else {
            errorMsg = message;
          }
        } else if (res?.error) {
          errorMsg = res.error;
        }
        
        console.error("❌ [Reports] API Error:", {
          errorType: res?.errorType,
          status: res?.status,
          message: res?.message,
          fullResponse: res
        });
        
        toastMessage(errorMsg, "error");
        setUsers([]);
        setLoading(false);
        return;
      }
      
      // Handle successful response according to API spec
      // Expected format: { data: [], totalPage, totalData, perPage, currentPage }
      // Note: axios.get() returns response.data, so if API returns { data: [...] },
      // then res = { data: [...] } directly
      if (res && typeof res === 'object') {
        // Check if response has error field (API error response)
        if (res.error) {
          console.error("❌ [Reports] API returned error:", res.error);
          toastMessage(res.error, "error");
          setUsers([]);
          setLoading(false);
          return;
        }
        
        // 🔍 Check for nested data structure (in case axios returns response.data.data)
        let reportsData = res.data;
        let paginationData = res;
        
        // If res.data exists but res.data.data also exists, use nested structure
        if (res.data && res.data.data && Array.isArray(res.data.data)) {
          console.log("🔍 [Reports] Found nested data structure (res.data.data)");
          reportsData = res.data.data;
          paginationData = res.data;
        }
        
        // Check if data exists and is an array
        if (reportsData && Array.isArray(reportsData)) {
          console.log("✅ [Reports] Received", reportsData.length, "reports");
          console.log("🔍 [Reports] First report sample:", reportsData[0]);
          
          try {
            // Map data according to API spec structure
            const mappedReports = reportsData.map((item, index) => {
              console.log(`🔍 [Reports] Mapping item ${index}:`, item);
              // Handle null ibsAssessment as per API spec
              const ibsAssessment = item.ibsAssessment || null;
              
              return {
                ...item,
                // Flatten user data for table display
                fullname: item.user?.fullname || "",
                phone: item.user?.phone || "",
                // Handle IBS assessment data (can be null)
                assessmentType: ibsAssessment?.assessmentType || null,
                assessmentDate: ibsAssessment?.assessmentDate 
                  ? formatDate(ibsAssessment.assessmentDate) 
                  : "",
                fileLocation: ibsAssessment?.fileLocation || null,
                // Document URL - primary document field from API
                document: item.document || null,
                action: { edit: false, delete: true },
              };
            });
            
            console.log("✅ [Reports] Mapped reports:", mappedReports.length);
            console.log("🔍 [Reports] First mapped report:", mappedReports[0]);
            
            setUsers(mappedReports);
            
            // Set pagination data from API response
            setPageCount(paginationData.totalPage || 1);
            setTotalData(paginationData.totalData || 0);
            setPerPage(paginationData.perPage || 10);
            setCurrentPage(paginationData.currentPage || 1);
            
            console.log("🔍 [Reports] Pagination set:", {
              pageCount: paginationData.totalPage || 1,
              totalData: paginationData.totalData || 0,
              perPage: paginationData.perPage || 10,
              currentPage: paginationData.currentPage || 1
            });
            
            console.log("✅ [Reports] Reports loaded successfully");
          } catch (mappingError) {
            console.error("❌ [Reports] Error mapping reports:", mappingError);
            console.error("❌ [Reports] Mapping error stack:", mappingError.stack);
            console.error("❌ [Reports] Original data:", reportsData);
            toastMessage("Error processing report data", "error");
            setUsers([]);
          }
        } else {
          // Empty data or unexpected format
          console.warn("⚠️ [Reports] No reports found or unexpected format");
          console.warn("⚠️ [Reports] reportsData:", reportsData);
          console.warn("⚠️ [Reports] reportsData type:", typeof reportsData);
          console.warn("⚠️ [Reports] reportsData is array?", Array.isArray(reportsData));
          
          // Check if it's an empty array
          if (Array.isArray(reportsData) && reportsData.length === 0) {
            console.log("ℹ️ [Reports] Empty array - no reports in database");
            toastMessage("No reports found", "info");
          } else {
            console.error("❌ [Reports] Unexpected data format");
            toastMessage("Unexpected response format from server", "error");
          }
          
          setUsers([]);
          setPageCount(1);
          setTotalData(0);
          setPerPage(10);
          setCurrentPage(1);
        }
      } else {
        console.error("❌ [Reports] Invalid response format:", res);
        toastMessage("Invalid response from server", "error");
        setUsers([]);
      }
      
      setLoading(false);
    } catch (err) {
      console.error("❌ [Reports] Exception in fetchUsers:", err);
      console.error("❌ [Reports] Error details:", {
        name: err?.name,
        message: err?.message,
        status: err?.response?.status || err?.status,
        isAxiosError: err?.isAxiosError,
        stack: err?.stack
      });
      
      // Handle AxiosError that wasn't caught by axios handler
      if (err?.isAxiosError || err?.name === "AxiosError") {
        const status = err?.response?.status || err?.status;
        let errorMsg = "Failed to load reports";
        
        if (status === 404) {
          errorMsg = "Reports endpoint not found. The backend API endpoint may not be implemented yet.";
        } else if (status === 401) {
          errorMsg = "Unauthorized. Please log in again.";
        } else if (status === 500) {
          errorMsg = "Server error. Please try again later.";
        } else {
          errorMsg = `Request failed with status ${status || "Unknown"}. Please try again.`;
        }
        
        toastMessage(errorMsg, "error");
      } else {
        toastMessage(`Failed to load reports: ${err?.message || "Unknown error"}`, "error");
      }
      
      setLoading(false);
      setUsers([]);
    }
  };
  function formatDate(dateString) {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
    const year = date.getFullYear();
    
    return `${day}-${month}-${year}`;
  }
  useEffect(() => {
    // Fetch reports when search term changes or page changes
    if (search === "") {
      fetchUsers("");
    } else if (debouncedSearch) {
      fetchUsers(debouncedSearch);
    }
    // Note: If search has value but debouncedSearch is not ready yet, wait for debounce
  }, [search, debouncedSearch, message, page]);

  const openModal = (type, dataForEdit) => {
    if (type === "add") {
      // setIsModalOpen(true);
    } else if (type === "edit") {
      // setEditModal(true);
      // setEditData(dataForEdit);
    } else if (type === "delete") {
      setDeleteModalOpen(true);
      setDeleteUser(dataForEdit);
    }
  };

  const handleSearch = (searchText) => {
   setSearch(searchText.trim());
    setPage(1);
  };

  const handleChange = (page) => {
    setPage(page);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
  };

  const handleDelete = (row) => {
    openModal("delete", row);
  };

  const handleDeleteUser = async (row) => {
    try {
      console.log("🗑️ [Reports] Deleting report:", row._id);
      
      const response = await patch(
        `/api/dashboard/appFeatures/deleteReport/${row._id}`
      );
      
      console.log("📥 [Reports] Delete response:", response);
      
      // Handle error response from axios error handler
      if (response?.response === "Failure") {
        const errorMsg = response?.message || response?.error || "Failed to delete report";
        console.error("❌ [Reports] Delete error:", errorMsg);
        toastMessage(errorMsg, "error");
        setDeleteModalOpen(false);
        return;
      }
      
      // Handle API error response format
      if (response?.error) {
        console.error("❌ [Reports] API returned error:", response.error);
        toastMessage(response.error, "error");
        setDeleteModalOpen(false);
        return;
      }
      
      // Success response according to API spec: { message: "Report deleted successfully" }
      const successMsg = response?.message || "Report deleted successfully";
      console.log("✅ [Reports] Report deleted:", successMsg);
      
      setMessage(successMsg);
      toastMessage(successMsg, "success");
      
      // Refresh the reports list
      fetchUsers(search || "");
      
    } catch (error) {
      console.error("❌ [Reports] Exception during delete:", error);
      const errorMsg = error?.message || error?.error || "Something went wrong!";
      toastMessage(errorMsg, "error");
    }
    
    setDeleteModalOpen(false);
  };

  const handleDisplay = (row) => {
    // Open document according to API spec - document field contains the URL
    const documentUrl = row?.document;
    
    if (documentUrl && typeof documentUrl === 'string') {
      console.log("📄 [Reports] Opening document:", documentUrl);
      // Open document in a new tab/window
      window.open(documentUrl, '_blank', 'noopener,noreferrer');
    } else {
      console.warn("⚠️ [Reports] No document URL available for report:", row?._id);
      toastMessage("Document not available", "error");
    }
  };

  return (
    <>
      <Layout>
        <div style={{ padding: "1rem" }}>
          <Typography variant="h5">Test Reports</Typography>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ width: "40%" }}>
              <Searchbar
                search={handleSearch}
                placeholder={"Search by document name"}
                debounceTime={1000}
              />
            </div>
          </div>
          <CustomTable
            data={users}
            columns={reportTableColumns}
            handlePageChange={(page) => handleChange(page)}
            handleDelete={handleDelete}
            handleDisplay={handleDisplay}
            pageNumber={page}
            pageCount={pageCount}
            totalData={totalData}
            perPage={perPage}
            currentPage={currentPage}
            loading={loading}
          />
        </div>
      </Layout>
      <DeleteModal
        open={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onDelete={handleDeleteUser}
        data={deleteUser}
      />
    </>
  );
};

export default Users;
